import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import queryString from 'query-string';
// api + models + helpers
import api from '@/api';
import { makeSelectEventById, makeSelectEventRoleForEvent } from '@/models/event';
import { selectCurrentUser } from '@/models/account';
import { selectLoading } from '@/core/loading';
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
import { _isEmpty } from '@/utils/jsHelpers/obj';
// components
import LandingPageTemplateContainer from '@/components/landing-page-templates/LandingPageTemplateContainer';
// types
import { IEventVisibilityOptions } from '@/models/event/types';
import Helmet from 'react-helmet';
import loadable from '@/utils/loadable';
import FullScreenLoader from '@/components/ui/FullScreenLoader';
import {
  initLogRocket,
  shouldTrackAllUsersInLogrocket,
} from '@/utils/logrocket';
const EventCanceledPage = loadable(
  () => import('@/components/EventCanceledPage'),
  {
    fallback: <FullScreenLoader />,
  },
);

function EventLandingPage({ isSpeakerPage = false }) {
  const dispatch = useDispatch();
  const { eventId } = useParams();

  const { search } = useLocation();
  const searchParams = queryString.parse(search);
  const event = useMemoizedSelector(makeSelectEventById, eventId);
  const [eventRefDetails, setEventRefDetails] = useState({});
  const currentUser = useSelector(selectCurrentUser);
  const currentUserEventPermissions = useMemoizedSelector(
    makeSelectEventRoleForEvent,
    eventId,
  );

  useEffect(() => {
    if (!event?.organizationId) {
      return;
    }
    if (shouldTrackAllUsersInLogrocket(event?.organizationId)) {
      initLogRocket();
    }
  }, [event?.organizationId]);

  useEffect(() => {
    if (!eventId) {
      return;
    }
    dispatch({
      type: 'event/getById',
      payload: {
        eventId: eventId,
      },
    });
  }, [eventId]);

  const loading = useSelector(selectLoading);

  const [gaTrackingId, setGaTrackingId] = useState();
  useEffect(() => {
    api.event.getGaTrackingId(eventId)
      .then(({ data: gaTrackingId }) => {
        setGaTrackingId(gaTrackingId);
      });
  }, [eventId]);

  // Get the EventRole object if it exists
  useEffect(() => {
    // Return if no currentUser OR if currentUserEventPermissions is already set
    if (
      !currentUser ||
      _isEmpty(currentUser)
    ) {
      return;
    }
    dispatch({
      type: 'event/getEventRole',
      payload: {
        eventId,
      },
    });
    dispatch({
      type: 'event/getUserDayAccessListByEventId',
      payload: {
        eventId,
      },
    });
  }, [eventId, currentUser]);

  // Get EventRole token if it exists
  useEffect(() => {
    (async () => {
      if (currentUser.accountId) {
        dispatch({
          type: 'account/getEventToken',
          payload: {
            eventId,
          },
        });
      }
    })();
  }, [eventId, currentUser]);

  // Set the role and status for this account
  const { role, status } = (() => {
    const permissions = currentUser.accountId
      ? currentUserEventPermissions
      : eventRefDetails;
    return { role: permissions.role, status: permissions.status };
  })();

  // Get the event visibility (NOTE - All of our events listed as PRIVATE are test events so this is not really used.)
  const [eventVisibility, setEventVisibility] = useState();
  useEffect(() => {
    api.event.getEventVisibility(eventId).then(({ data }) => {
      setEventVisibility(data);
    });
  }, []);

  // TODO - this should be replaced with the components for private events with no access
  if (eventVisibility === IEventVisibilityOptions.PRIVATE && !role) {
    return <div>We're sorry. You do not have access to this event.</div>;
  }

  // NOTE - we are waiting for the event role request to finish before rendering the landing page
  if (loading.effects['event/getEventRole'] || gaTrackingId === undefined) {
    return <></>;
  }

  const landingPageProps = {
    eventId,
    currentUser,
    role,
    status,
    eventRefDetails,
    eventVisibility,
    isSpeakerPage,
  };

  if (event && event.status === 'CANCELED') {
    return <EventCanceledPage event={event} />;
  }

  if (gaTrackingId) {
    return (
      <>
        <Helmet>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}></script>
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gaTrackingId}');
            `}
          </script>
        </Helmet>
        <LandingPageTemplateContainer {...landingPageProps} />
      </>
    );
  }

  return <LandingPageTemplateContainer {...landingPageProps} />;
}

export default memo(EventLandingPage);
